import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format RUT chileno
export function formatRut(rut) {
  if (!rut) return '';
  const cleanRut = rut.replace(/[^0-9kK]/g, '');
  if (cleanRut.length < 2) return cleanRut;
  
  const body = cleanRut.slice(0, -1);
  const verifier = cleanRut.slice(-1).toUpperCase();
  
  let formatted = '';
  for (let i = body.length - 1, j = 0; i >= 0; i--, j++) {
    if (j > 0 && j % 3 === 0) {
      formatted = '.' + formatted;
    }
    formatted = body[i] + formatted;
  }
  
  return `${formatted}-${verifier}`;
}

// Validate RUT chileno
export function validateRut(rut) {
  if (!rut) return false;
  const cleanRut = rut.replace(/[^0-9kK]/g, '');
  if (cleanRut.length < 2) return false;
  
  const body = cleanRut.slice(0, -1);
  const verifier = cleanRut.slice(-1).toUpperCase();
  
  let sum = 0;
  let multiplier = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = sum % 11;
  const calculatedVerifier = remainder === 0 ? '0' : remainder === 1 ? 'K' : String(11 - remainder);
  
  return verifier === calculatedVerifier;
}

// Format currency (Chilean Peso)
export function formatCurrency(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

// Format date
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
}

// Format date with time
export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

// Get stock status
export function getStockStatus(quantity, minStock) {
  if (quantity <= minStock / 2) return { status: 'critical', label: 'Crítico', color: 'destructive' };
  if (quantity <= minStock) return { status: 'low', label: 'Bajo', color: 'warning' };
  return { status: 'ok', label: 'Normal', color: 'success' };
}

// Get order status label
export function getOrderStatusLabel(status) {
  const labels = {
    draft: 'Borrador',
    approved: 'Aprobada',
    sent: 'Enviada',
    received: 'Recibida',
    cancelled: 'Cancelada'
  };
  return labels[status] || status;
}

// Get invoice status label
export function getInvoiceStatusLabel(status) {
  const labels = {
    pending: 'Pendiente',
    paid: 'Pagada',
    cancelled: 'Anulada'
  };
  return labels[status] || status;
}

// Chilean communes list (simplified)
export const CHILEAN_COMMUNES = [
  'Santiago', 'Providencia', 'Las Condes', 'Vitacura', 'Lo Barnechea',
  'Ñuñoa', 'La Reina', 'Peñalolén', 'Macul', 'San Joaquín',
  'La Florida', 'Puente Alto', 'La Granja', 'San Ramón', 'San Miguel',
  'La Cisterna', 'El Bosque', 'Pedro Aguirre Cerda', 'Lo Espejo', 'Cerrillos',
  'Maipú', 'Estación Central', 'Cerro Navia', 'Quinta Normal', 'Lo Prado',
  'Pudahuel', 'Renca', 'Quilicura', 'Recoleta', 'Independencia',
  'Conchalí', 'Huechuraba', 'Valparaíso', 'Viña del Mar', 'Concepción',
  'Talcahuano', 'Temuco', 'Antofagasta', 'Rancagua', 'Talca',
  'Arica', 'Chillán', 'Iquique', 'Puerto Montt', 'Coquimbo',
  'La Serena', 'Osorno', 'Valdivia', 'Punta Arenas', 'Copiapó'
];

// Housing types
export const HOUSING_TYPES = [
  'Casa',
  'Departamento',
  'Pieza',
  'Mediagua',
  'Otro'
];

// Housing ownership
export const HOUSING_OWNERSHIP = [
  'Propia pagada',
  'Propia pagando',
  'Arrendada',
  'Cedida',
  'Allegado',
  'Ocupación irregular',
  'Otro'
];

// Education levels
export const EDUCATION_LEVELS = [
  'Sin educación formal',
  'Básica incompleta',
  'Básica completa',
  'Media incompleta',
  'Media completa',
  'Técnica incompleta',
  'Técnica completa',
  'Universitaria incompleta',
  'Universitaria completa',
  'Postgrado'
];

// Employment status
export const EMPLOYMENT_STATUS = [
  'Empleado',
  'Trabajador independiente',
  'Cesante',
  'Buscando trabajo primera vez',
  'Jubilado/Pensionado',
  'Estudiante',
  'Dueña de casa',
  'Incapacitado permanente',
  'Otro'
];

// RSH Tramos
export const RSH_TRAMOS = [
  '0% - 40%',
  '41% - 50%',
  '51% - 60%',
  '61% - 70%',
  '71% - 80%',
  '81% - 90%',
  '91% - 100%'
];

// Aid types
export const AID_TYPES = [
  'Alimentos',
  'Medicamentos',
  'Pago de servicios',
  'Arriendo',
  'Vestuario',
  'Materiales de construcción',
  'Ayuda técnica',
  'Emergencia',
  'Otro'
];

// Product categories
export const PRODUCT_CATEGORIES = [
  'Alimentos',
  'Medicamentos',
  'Vestuario',
  'Artículos de aseo',
  'Materiales',
  'Equipamiento',
  'Otro'
];

// Product units
export const PRODUCT_UNITS = [
  'Unidad',
  'Kilo',
  'Litro',
  'Caja',
  'Paquete',
  'Bolsa',
  'Par'
];

// User roles
export const USER_ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'social_worker', label: 'Asistencia Social' },
  { value: 'warehouse', label: 'Bodega' },
  { value: 'finance', label: 'Finanzas' }
];

// Role permissions
export const ROLE_PERMISSIONS = {
  admin: {
    modules: ['dashboard', 'beneficiaries', 'inventory', 'deliveries', 'suppliers', 'invoices', 'purchase_orders', 'users', 'attention', 'reports'],
    label: 'Administrador'
  },
  social_worker: {
    modules: ['dashboard', 'beneficiaries', 'attention', 'deliveries'],
    label: 'Asistencia Social'
  },
  warehouse: {
    modules: ['dashboard', 'inventory', 'deliveries'],
    label: 'Bodega'
  },
  finance: {
    modules: ['dashboard', 'suppliers', 'invoices', 'purchase_orders', 'reports'],
    label: 'Finanzas'
  }
};

// Basic services
export const BASIC_SERVICES = [
  'Agua potable',
  'Alcantarillado',
  'Electricidad',
  'Gas',
  'Internet',
  'Teléfono fijo'
];

// Marital status
export const MARITAL_STATUS = [
  'Soltero/a',
  'Casado/a',
  'Conviviente',
  'Divorciado/a',
  'Viudo/a',
  'Separado/a'
];

// Family relationships
export const FAMILY_RELATIONSHIPS = [
  'Cónyuge',
  'Conviviente',
  'Hijo/a',
  'Padre',
  'Madre',
  'Hermano/a',
  'Abuelo/a',
  'Nieto/a',
  'Sobrino/a',
  'Tío/a',
  'Otro familiar',
  'No familiar'
];
